import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import { getFormTypes, getFormDefinition, createTicket, addAttachment, getUserProducts, getOrgUsers } from "../../services/supportApi";
import DynamicField from "./DynamicField";
import "./SupportForm.css";

function SupportForm({ fonts, isMobile, onTicketCreated }) {
  const [formTypes, setFormTypes] = useState([]);
  const [selectedFormType, setSelectedFormType] = useState(null);
  const [formDefinition, setFormDefinition] = useState(null);
  const [subject, setSubject] = useState("");
  const [fieldValues, setFieldValues] = useState({});
  const [fileFields, setFileFields] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [orgUsers, setOrgUsers] = useState([]);
  const [ccUserIds, setCcUserIds] = useState([]);

  // Fetch form types on mount
  useEffect(() => {
    fetchFormTypes();
    fetchOrgUsers();
  }, []);

  // Fetch form definition when type is selected
  useEffect(() => {
    if (selectedFormType) {
      fetchFormDefinition(selectedFormType);
    }
  }, [selectedFormType]);

  const fetchFormTypes = async () => {
    try {
      const response = await getFormTypes();
      setFormTypes(response.data);
      // Auto-select if only one form type
      if (response.data.length === 1) {
        setSelectedFormType(response.data[0].slug);
      }
      setLoading(false);
    } catch (error) {
      console.log("Error fetching form types:", error);
      setLoading(false);
    }
  };

  const fetchFormDefinition = async (slug) => {
    try {
      setLoading(true);
      const response = await getFormDefinition(slug);
      setFormDefinition(response.data);
      // Reset field values when form type changes
      setSubject("");
      setFieldValues({});
      setFileFields({});
      setErrors({});
      setSubmitResult(null);
      setSelectedProduct("");

      // Fetch products if product_request is enabled
      if (response.data.product_request) {
        fetchUserProducts();
      } else {
        setProducts([]);
      }

      setLoading(false);
    } catch (error) {
      console.log("Error fetching form definition:", error);
      setLoading(false);
    }
  };

  const fetchUserProducts = async () => {
    try {
      const response = await getUserProducts();
      setProducts(response.data);
    } catch (error) {
      console.log("Error fetching products:", error);
      setProducts([]);
    }
  };

  const fetchOrgUsers = async () => {
    try {
      const response = await getOrgUsers();
      setOrgUsers(response.data || []);
    } catch (error) {
      console.log("Error fetching org users:", error);
      setOrgUsers([]);
    }
  };

  const toggleCcUser = (id) => {
    setCcUserIds((prev) =>
      prev.includes(id) ? prev.filter((uid) => uid !== id) : [...prev, id]
    );
  };

  const handleFieldChange = (fieldName, value) => {
    setFieldValues((prev) => ({
      ...prev,
      [fieldName]: value
    }));
    // Clear error when field is modified
    if (errors[fieldName]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleFileChange = (fieldName, files) => {
    setFileFields((prev) => ({
      ...prev,
      [fieldName]: files
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Subject is always required
    if (!subject || subject.trim() === "") {
      newErrors.subject = "Subject is required";
    }

    if (!formDefinition || !formDefinition.fields) {
      return false;
    }

    formDefinition.fields.forEach((field) => {
      const value = fieldValues[field.name];

      // Required validation
      if (field.required) {
        if (field.field_type === "file") {
          if (!fileFields[field.name] || fileFields[field.name].length === 0) {
            newErrors[field.name] = "This field is required";
          }
        } else if (field.field_type === "checkbox") {
          if (!Array.isArray(value) || value.length === 0) {
            newErrors[field.name] = "This field is required";
          }
        } else if (!value || (typeof value === "string" && value.trim() === "")) {
          newErrors[field.name] = "This field is required";
        }
      }

      // Email validation
      if (field.field_type === "email" && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          newErrors[field.name] = "Please enter a valid email address";
        }
      }

      // Number validation
      if (field.field_type === "number" && value) {
        if (isNaN(Number(value))) {
          newErrors[field.name] = "Please enter a valid number";
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const uploadFiles = async (ticketId) => {
    for (const [fieldName, files] of Object.entries(fileFields)) {
      for (const file of files) {
        try {
          await addAttachment(ticketId, file, fieldName);
        } catch (error) {
          console.log("Error uploading file:", error);
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check authentication
    const cookie = Cookies.get("_udxdworkid");
    if (!cookie) {
      setSubmitResult({
        type: "error",
        message: "You must be signed in to submit a support ticket."
      });
      return;
    }

    // Validate form
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setSubmitResult(null);

    try {
      const ticketData = {
        form_type: selectedFormType,
        subject: subject,
        field_values: fieldValues
      };

      // Include product if selected
      if (selectedProduct) {
        ticketData.product = parseInt(selectedProduct, 10);
      }

      if (ccUserIds.length > 0) {
        ticketData.cc_user_ids = ccUserIds;
      }

      const response = await createTicket(ticketData);

      const ticketId = response.data.id;

      // Upload files if any
      if (Object.keys(fileFields).length > 0) {
        await uploadFiles(ticketId);
      }

      setSubmitResult({
        type: "success",
        message: "Your support ticket has been submitted successfully. We'll get back to you soon."
      });

      // Reset form
      setSubject("");
      setFieldValues({});
      setFileFields({});
      setErrors({});
      setSelectedProduct("");
      setCcUserIds([]);

      // Notify parent to refresh ticket list
      if (onTicketCreated) {
        onTicketCreated();
      }

    } catch (error) {
      console.log("Error submitting ticket:", error);
      setSubmitResult({
        type: "error",
        message: error.response?.data?.detail || "An error occurred while submitting your ticket. Please try again."
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormTypeChange = (e) => {
    setSelectedFormType(e.target.value);
  };

  const handleNewTicket = () => {
    setSubmitResult(null);
    setSubject("");
    setFieldValues({});
    setFileFields({});
    setErrors({});
    setSelectedProduct("");
    setCcUserIds([]);
  };

  if (loading && formTypes.length === 0) {
    return <div className="supportForm loading">Loading...</div>;
  }

  // Success state
  if (submitResult?.type === "success") {
    return (
      <div className="supportForm successMessage">
        <h2>Thank You!</h2>
        <p>{submitResult.message}</p>
        <button
          type="button"
          className="submitBtn"
          onClick={handleNewTicket}
        >
          Submit Another Ticket
        </button>
      </div>
    );
  }

  return (
    <div className="supportFormContainer">
      {/* Form type selector (only if multiple types) */}
      {formTypes.length > 1 && (
        <div className="formField formTypeSelector">
          <label htmlFor="formType">
            Select ticket type
            <span className="required">*</span>
          </label>
          <select
            id="formType"
            value={selectedFormType || ""}
            onChange={handleFormTypeChange}
          >
            <option value="">Choose a ticket type</option>
            {formTypes.map((type) => (
              <option key={type.slug} value={type.slug}>
                {type.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Form description */}
      {formDefinition && formDefinition.description && (
        <p className="formDescription">{formDefinition.description}</p>
      )}

      {/* Error feedback */}
      {submitResult?.type === "error" && (
        <div className="errorMessage">
          {submitResult.message}
        </div>
      )}

      {/* Dynamic form fields */}
      {formDefinition && formDefinition.fields && (
        <form className="supportForm" onSubmit={handleSubmit}>
          {/* CC teammates - only when the user's org has other members */}
          {orgUsers.length > 0 && (
            <div className="formField">
              <label>CC on this ticket</label>
              <p className="helpText">They&rsquo;ll receive every notification on this ticket, including replies and status updates.</p>
              <div className="checkboxGroup">
                {orgUsers.map((u) => (
                  <label key={u.id} className="checkboxOption">
                    <input
                      type="checkbox"
                      checked={ccUserIds.includes(u.id)}
                      onChange={() => toggleCcUser(u.id)}
                    />
                    <span>
                      {u.name}
                      {u.email ? ` <${u.email}>` : ""}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Product dropdown - shown when product_request is enabled */}
          {formDefinition.product_request && products.length > 0 && (
            <div className="formField">
              <label htmlFor="product">
                Product
              </label>
              <select
                id="product"
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
              >
                <option value="">Select a product</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Subject field - always first */}
          <div className={`formField ${errors.subject ? "hasError" : ""}`}>
            <label htmlFor="subject">
              Subject
              <span className="required">*</span>
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                if (errors.subject) {
                  setErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.subject;
                    return newErrors;
                  });
                }
              }}
              placeholder="Brief summary of your request"
            />
            {errors.subject && <span className="errorText">{errors.subject}</span>}
          </div>

          {formDefinition.fields
            .sort((a, b) => a.ordering - b.ordering)
            .map((field) => (
              <DynamicField
                key={field.id || field.name}
                field={field}
                value={fieldValues[field.name]}
                onChange={handleFieldChange}
                onFileChange={handleFileChange}
                error={errors[field.name]}
                isMobile={isMobile}
              />
            ))}

          <div className="formField">
            <button
              type="submit"
              className="submitBtn"
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Submit Ticket"}
            </button>
          </div>
        </form>
      )}

      {/* No form selected state */}
      {!formDefinition && formTypes.length > 1 && (
        <p className="selectPrompt">Please select a ticket type above to continue.</p>
      )}

      {/* No form types available */}
      {formTypes.length === 0 && !loading && (
        <p className="noForms">No support forms are currently available.</p>
      )}
    </div>
  );
}

export default SupportForm;
